import { loadBusinessCenterOperationalState, saveBusinessCenterOperationalState, type BusinessCenterOperationalStateScope } from "./businessCenterOperationalStateApi";

export interface BusinessCenterOperationalStateAdapter<T> {
  moduleKey: string;
  scope?: BusinessCenterOperationalStateScope;
  seed: () => T;
  normalize: (value: T) => T;
  isEmpty?: (value: T) => boolean;
}

export function createBusinessCenterOperationalStateStore<T>(adapter: BusinessCenterOperationalStateAdapter<T>) {
  let cache: T | null = adapter.normalize(adapter.seed());
  let hasTrustedState = false;
  let refreshPromise: Promise<T> | null = null;

  function read() {
    return cache ? adapter.normalize(cache) : adapter.normalize(adapter.seed());
  }

  async function refresh() {
    if (refreshPromise) return refreshPromise;
    refreshPromise = (async () => {
      let response: Awaited<ReturnType<typeof loadBusinessCenterOperationalState<T>>> | null = null;
      try {
        response = await loadBusinessCenterOperationalState<T>(adapter.moduleKey, adapter.scope || {});
        const remote = response.state;
        if (remote && !(adapter.isEmpty?.(remote) ?? false)) {
          cache = adapter.normalize(remote);
          hasTrustedState = true;
          return cache;
        }
      } catch (error) {
        if (hasTrustedState && cache) return adapter.normalize(cache);
        throw error;
      }
      const seed = adapter.normalize(adapter.seed());
      cache = seed;
      if (!response.record) {
        await saveBusinessCenterOperationalState(adapter.moduleKey, seed, adapter.scope || {}).catch(() => undefined);
      }
      hasTrustedState = true;
      return cache;
    })().finally(() => {
      refreshPromise = null;
    });
    return refreshPromise;
  }

  function write(next: T) {
    cache = adapter.normalize(next);
    hasTrustedState = true;
    void saveBusinessCenterOperationalState(adapter.moduleKey, cache, adapter.scope || {}).catch(() => undefined);
    return cache;
  }

  async function writeAsync(next: T) {
    const normalized = adapter.normalize(next);
    await saveBusinessCenterOperationalState(adapter.moduleKey, normalized, adapter.scope || {});
    cache = normalized;
    hasTrustedState = true;
    return cache;
  }

  function patch(mutator: (current: T) => T) {
    return write(mutator(read()));
  }

  return {
    read,
    refresh,
    write,
    writeAsync,
    patch,
  };
}
